// v3 guided-flow step model: a pure function of calculator state -> the ordered steps, each with
// a status and a locked flag. The progress ribbon, next/previous and the skip warnings all read it
// (spec "Guided flow"; stories 7-12, 26, 62 of the decision log).
//
// No React, no store import: the store imports this file (for next / previous), not the reverse.
// Takes a plain `StepModelInput` so tests build state without mounting anything.

import { SIZED_USE_CASES, getSizedUseCase, type MaturityTier } from '../data/use-case-catalog';
import {
  DEFAULT_DEAL,
  countInputsOnDefaults,
  orderSelection,
  requiresCurrentAnnualPipeline,
  type CalculatorInput,
  type UseCaseSelection,
} from '../engine/use-case-engine';

/* ---------------------------------------------------------------------------- types */

/** `null` = the homepage (title card), before a business case is started. */
export type FlowPath = 'business-case';

export type AccountSelection =
  | { kind: 'demo' }
  | {
      kind: 'account';
      accountId: string;
      accountName: string;
      opportunityId?: string;
      opportunityName?: string;
    };

export type StepStatus = 'not-started' | 'in-progress' | 'using-defaults' | 'complete';

export type StepKind =
  | 'title'
  | 'account'
  | 'selection'
  | 'deal'
  | 'use-case'
  | 'review'
  | 'roi';

export interface Step {
  id: string;
  kind: StepKind;
  label: string;
  /** Set on `use-case` steps only. */
  useCaseId?: string;
  tier?: MaturityTier;
  status: StepStatus;
  /** Business-case steps after the account step stay locked until an account or demo is chosen. */
  locked: boolean;
}

export interface StepModelInput {
  path: FlowPath | null;
  account: AccountSelection | null;
  deal: CalculatorInput['deal'];
  selected: readonly UseCaseSelection[];
  tools?: CalculatorInput['tools'];
  customTools?: CalculatorInput['customTools'];
  /** Ids of steps the SE has opened. "Not started" vs "in progress" needs this; the data alone can't say. */
  visited: readonly string[];
}

/* ------------------------------------------------------------------------------ ids */

export const TITLE_STEP = 'title';
export const ACCOUNT_STEP = 'account';
export const SELECTION_STEP = 'use-cases';
export const DEAL_STEP = 'deal';
export const REVIEW_STEP = 'review';
export const ROI_STEP = 'roi';

export const stepIdForUseCase = (useCaseId: string): string => `uc:${useCaseId}`;

/** The use case id behind a `uc:<id>` step id, or null. */
export function useCaseIdFromStepId(stepId: string): string | null {
  return stepId.startsWith('uc:') ? stepId.slice(3) : null;
}

/* ------------------------------------------------------------------------- statuses */

const visitedStatus = (visited: boolean, done: boolean): StepStatus =>
  done ? 'complete' : visited ? 'in-progress' : 'not-started';

function statusOfUseCase(input: StepModelInput, sel: UseCaseSelection, visited: boolean): StepStatus {
  const { assumed, total } = countInputsOnDefaults(
    sel.id,
    sel.answers,
    sel.recordLevels,
    { tools: input.tools, customTools: input.customTools },
    sel.methods,
  );
  if (total > 0 && assumed === 0) return 'complete';
  if (assumed === total) return visited ? 'using-defaults' : 'not-started';
  return 'in-progress';
}

function dealStatus(input: StepModelInput, visited: boolean): StepStatus {
  if (!visited) return 'not-started';
  const ids = input.selected.map((s) => s.id);
  // Every deal input carries a default, so "still on the defaults" is the only honest signal. Pipeline
  // only counts when a selected use case reads it.
  const pipelineOnDefault =
    !requiresCurrentAnnualPipeline(ids) || input.deal.currentAnnualPipeline === DEFAULT_DEAL.currentAnnualPipeline;
  const onDefaults =
    input.deal.acv === DEFAULT_DEAL.acv && input.deal.winRate === DEFAULT_DEAL.winRate && pipelineOnDefault;
  return onDefaults ? 'using-defaults' : 'complete';
}

function reviewStatus(input: StepModelInput, visited: boolean): StepStatus {
  if (!visited) return 'not-started';
  const anyAssumed = input.selected.some(
    (s) =>
      countInputsOnDefaults(
        s.id,
        s.answers,
        s.recordLevels,
        { tools: input.tools, customTools: input.customTools },
        s.methods,
      ).assumed > 0,
  );
  return anyAssumed ? 'using-defaults' : 'complete';
}

/* ------------------------------------------------------------------------- the model */

/**
 * The ordered steps for the current path.
 *  - No path: just the homepage (title card).
 *  - Business case: account -> use case selection -> deal economics -> one step per selected use
 *    case in tier order (Crawl, Walk, Run, Efficiency) -> review -> ROI summary (the executive deck opens from there).
 */
export function buildSteps(input: StepModelInput): Step[] {
  const seen = new Set(input.visited);
  const was = (id: string) => seen.has(id);
  const simple = (id: string, kind: StepKind, label: string): Step => ({
    id,
    kind,
    label,
    status: was(id) ? 'complete' : 'not-started',
    locked: false,
  });

  if (input.path === null) return [simple(TITLE_STEP, 'title', 'Start')];

  const unlocked = input.account !== null;
  const steps: Step[] = [
    {
      id: ACCOUNT_STEP,
      kind: 'account',
      label: 'Account',
      status: visitedStatus(was(ACCOUNT_STEP), unlocked),
      locked: false,
    },
    {
      id: SELECTION_STEP,
      kind: 'selection',
      label: 'Use cases',
      status: visitedStatus(was(SELECTION_STEP), input.selected.length > 0),
      locked: !unlocked,
    },
    {
      id: DEAL_STEP,
      kind: 'deal',
      label: 'Deal economics',
      status: dealStatus(input, was(DEAL_STEP)),
      locked: !unlocked,
    },
  ];

  for (const sel of orderSelection([...input.selected])) {
    const uc = getSizedUseCase(sel.id);
    if (!uc) continue;
    const id = stepIdForUseCase(uc.id);
    steps.push({
      id,
      kind: 'use-case',
      label: uc.name,
      useCaseId: uc.id,
      tier: uc.tier,
      status: statusOfUseCase(input, sel, was(id)),
      locked: !unlocked,
    });
  }

  steps.push(
    { id: REVIEW_STEP, kind: 'review', label: 'Review', status: reviewStatus(input, was(REVIEW_STEP)), locked: !unlocked },
    { id: ROI_STEP, kind: 'roi', label: 'ROI summary', status: was(ROI_STEP) ? 'complete' : 'not-started', locked: !unlocked },
  );
  return steps;
}

export interface StepNeighbors {
  current: Step | null;
  previous: Step | null;
  next: Step | null;
}

export function stepNeighbors(steps: readonly Step[], currentId: string): StepNeighbors {
  const i = steps.findIndex((s) => s.id === currentId);
  if (i < 0) return { current: null, previous: null, next: null };
  return { current: steps[i], previous: steps[i - 1] ?? null, next: steps[i + 1] ?? null };
}

/* ------------------------------------------------------------------- gates and warnings */

/**
 * Why "Next" is disabled on a step, or null. Only the use case selection holds the SE back (deal
 * economics all carry defaults now); the ribbon still
 * jumps freely (story 9), and an unfinished use case page never blocks (story 12).
 */
export function nextBlockReason(input: StepModelInput, stepId: string): string | null {
  if (input.path !== 'business-case') return null;
  if (stepId === SELECTION_STEP && input.selected.length === 0) return 'Pick at least one use case to continue';
  return null;
}

/** Story 25: Walk or Run picked with no Crawl use case. A soft warning, never a block. */
export function needsCrawlWarning(ids: readonly string[]): boolean {
  const tiers = ids.map((id) => getSizedUseCase(id)?.tier).filter((t): t is MaturityTier => t !== undefined);
  return tiers.some((t) => t === 'Walk' || t === 'Run') && !tiers.includes('Crawl');
}

/** The sized use case ids in step (tier) order — a convenience for the shell and tests. */
export function orderedUseCaseIds(selected: readonly UseCaseSelection[]): string[] {
  const ids = new Set(selected.map((s) => s.id));
  return SIZED_USE_CASES.filter((u) => ids.has(u.id)).map((u) => u.id);
}
